import get from "lodash/get";

import Tokens from "../semantic";
import { ColorTokenName } from "../types";

import TokensFallback from "./semantic-fallback";

export const getColorToken = <T extends string = ColorTokenName>(
  tokenName: T
) => get(Tokens, tokenName);

export const getColorTokenFallback = <T extends string = ColorTokenName>(
  tokenName: T
) => get(TokensFallback, tokenName);

export const getColorTokenWithOpacity = (
  tokenOrColor: string,
  alphaDecimal: number
) => `rgba(from ${tokenOrColor} r g b / ${alphaDecimal})`;

export const getColorTokenWithOpacityFallback = (
  tokenFallbackOrColor: string,
  alphaDecimal: number
) => {
  let fallbackColor: string | undefined = undefined;
  if (isRgb(tokenFallbackOrColor)) {
    fallbackColor = rgbToRgba(tokenFallbackOrColor, alphaDecimal);
  } else if (isRgba(tokenFallbackOrColor)) {
    fallbackColor = replaceRgbaOrHslaAlpha(tokenFallbackOrColor, alphaDecimal);
  } else if (isHsl(tokenFallbackOrColor)) {
    fallbackColor = hslToHsla(tokenFallbackOrColor, alphaDecimal);
  } else if (isHsla(tokenFallbackOrColor)) {
    fallbackColor = replaceRgbaOrHslaAlpha(tokenFallbackOrColor, alphaDecimal);
  } else if (isTransparentHex(tokenFallbackOrColor)) {
    fallbackColor = replaceTransparentHexAlpha(
      tokenFallbackOrColor,
      alphaDecimal
    );
  } else if (isShorthandHex(tokenFallbackOrColor)) {
    fallbackColor = hexToTransparentHex(
      shorthandHexToHex(tokenFallbackOrColor),
      alphaDecimal
    );
  } else if (isHex(tokenFallbackOrColor)) {
    fallbackColor = hexToTransparentHex(tokenFallbackOrColor, alphaDecimal);
  }
  return fallbackColor;
};

const alphaDecimalToBase16 = (alpha: number) =>
  Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0");

const isTransparentHex = (value: string) => /^#[a-f0-9]{8}$/i.test(value);

const isShorthandHex = (value: string) => /^#[a-f0-9]{3}$/i.test(value);

const isHex = (value: string) => /^#[a-f0-9]{6}$/i.test(value);

const isRgb = (value: string) =>
  /^rgb\(\s*\d+\s*[,\s]\s*\d+\s*[,\s]\s\d+\s*\)$/.test(value);

const isRgba = (value: string) =>
  /^rgba?\(\s*\d+\s*[,\s]\s*\d+\s*[,\s]\s*\d+\s*[,\s/]\s[\d.]+\s*%?\s*\)$/.test(
    value
  );

const isHsl = (value: string) =>
  /^hsl\(\s*[\d.]+\s*(deg|turn)?\s*[,\s]\s*[\d.]+\s*%\s*[,\s]\s*[\d.]+\s*%\s*\)$/.test(
    value
  );

const isHsla = (value: string) =>
  /^hsla?\(\s*[\d.]+\s*(deg|turn)?\s*[,\s]\s*[\d.]+\s*%\s*[,\s]\s*[\d.]+\s*%\s*[,/]\s*[\d.]+\s*%?\s*\)$/.test(
    value
  );

const hslToHsla = (hsl: string, alphaDecimal: number) => {
  const isLegacyFormat = hsl.includes(",");
  return hsl
    .replace("hsl(", "hsla(")
    .replace(
      ")",
      isLegacyFormat ? `, ${alphaDecimal})` : ` / ${alphaDecimal})`
    );
};

const rgbToRgba = (rgb: string, alphaDecimal: number) =>
  rgb.replace("rgb(", "rgba(").replace(")", `, ${alphaDecimal})`);

const shorthandHexToHex = (shorthandHex: string) =>
  "#" +
  Array.from(shorthandHex.substring(1))
    .map((char) => char.repeat(2))
    .join("");

const hexToTransparentHex = (hex: string, alphaDecimal: number) =>
  hex + alphaDecimalToBase16(alphaDecimal);

const replaceTransparentHexAlpha = (
  transparentHex: string,
  alphaDecimal: number
) =>
  transparentHex.substring(0, transparentHex.length - 2) +
  alphaDecimalToBase16(alphaDecimal);

const replaceRgbaOrHslaAlpha = (rgbaOrHsla: string, alphaDecimal: number) => {
  const isLegacyFormat = rgbaOrHsla.includes(",");
  const alphaStartIndex =
    rgbaOrHsla.lastIndexOf(isLegacyFormat ? "," : "/") + 1;
  const alphaEndIndex = rgbaOrHsla.lastIndexOf(")");
  return (
    rgbaOrHsla.substring(0, alphaStartIndex) +
    " " +
    alphaDecimal +
    rgbaOrHsla.substring(alphaEndIndex)
  );
};
